import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks";
import { Asterisk } from "lucide-react";
import { Link } from "react-router";
import { useCustomerState } from "@/hooks/useCustomerState";
import { useProfile } from "@/hooks";
import { cn } from "@/lib/utils";

/**
 * Credits component displays the user's remaining free credits and subscription credits
 * with a visual progress bar and informational text.
 * Fetches credits on mount - user can refresh by reopening the dropdown/sidebar.
 */
export function Credits({ className }: { className?: string }) {
  const { user } = useAuth();
  const { totalCredits: polarCredits, isLoading: loadingPolar } = useCustomerState();
  const { profile } = useProfile();
  const [freeCredits, setFreeCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Determine billing period based on subscription tier
  const billingPeriod = profile?.subscription_tier === 'weekly' ? 'weekly' : 'monthly';

  useEffect(() => {
    if (!user) return;

    const fetchCredits = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('free_credits')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        setFreeCredits(data?.free_credits ?? 0);
      } catch (error) {
        console.error('Error fetching credits:', error);
        setFreeCredits(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCredits();
  }, [user]);

  if (!user || isLoading || loadingPolar) {
    return (
      <div className={cn("flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground", className)}>
        <Asterisk className="h-4 w-4 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  const maxFreeCredits = 20;
  const totalCredits = (freeCredits ?? 0) + (polarCredits ?? 0);

  // Calculate percentages for the dual-color bar
  const maxDisplayCredits = Math.max(totalCredits, maxFreeCredits);
  const freePercentage = ((freeCredits ?? 0) / maxDisplayCredits) * 100;
  const polarPercentage = ((polarCredits ?? 0) / maxDisplayCredits) * 100;

  return (
    <Link
      to="/app/settings"
      className={cn("block px-3 py-2 hover:bg-accent/50 transition-colors rounded-md group", className)}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-foreground">Credits remaining</span>
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold text-foreground">
            {totalCredits.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Dual-color progress bar */}
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden flex">
        {/* Polar credits bar (primary/blue) */}
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${polarPercentage}%` }}
        />
        {/* Free credits bar (green/emerald) */}
        <div
          className="h-full bg-green-500 transition-all duration-300"
          style={{ width: `${freePercentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between mt-1.5">
        <p className="text-xs text-muted-foreground">
          {polarCredits ? <span className="text-primary">{polarCredits.toLocaleString()} {billingPeriod} + </span> : ''}
          <span className="text-emerald-600 dark:text-emerald-400">{freeCredits ?? 0} free</span>

        </p>
      </div>
    </Link>
  );
}
